/**
 * Real portfolio data — content ported verbatim from the live site's
 * #portfolio section (index.html) and cross-checked against
 * EA_Studio_Claude_Master_Prompt.txt §2.5. Problem/solution/result and
 * description copy: the live EN dictionary had drifted from the real BS
 * facts (different specifics per project, e.g. "sending customers to a
 * competitor's site" vs the actual "worked by referral only" story) — used
 * the accurate BS-sourced facts (also the ones the master prompt and this
 * app's own pricing/portfolio tags already agree on, e.g. Next.js 14 +
 * Supabase + 160+ photos for Tehnostan) translated consistently into EN
 * instead of the stale EN copy, rather than ship two projects with
 * different backstories per language.
 */

import tehnostanLogo from '@/assets/portfolio/tehnostan/tehnostan-logo.webp'
import tehnostanScreenshot from '@/assets/portfolio/tehnostan/tehnostan-screenshot.webp'
import tehnostanBefore from '@/assets/portfolio/tehnostan/tehnostan-before.webp'
import tehnostanAfter from '@/assets/portfolio/tehnostan/tehnostan-after.webp'
import tehnostanAd from '@/assets/portfolio/tehnostan/tehnostan-ad.webp'
import tehnostanAd2 from '@/assets/portfolio/tehnostan/tehnostan-ad2.webp'
import tehnostanCard from '@/assets/portfolio/tehnostan/Vizitka.webp'
import tehnostanFolijaStrana from '@/assets/portfolio/tehnostan/strana.webp'
import tehnostanFolijaZadnja from '@/assets/portfolio/tehnostan/zadnja.webp'
import tehnostanPoster from '@/assets/portfolio/tehnostan/poster.webp'
import mmMetalLogo from '@/assets/portfolio/mm-metal/mm-metal-logo.webp'
import mmMetalScreenshot from '@/assets/portfolio/mm-metal/mm-metal-screenshot.webp'
import namjestajJasarevicLogo from '@/assets/portfolio/namjestaj-jasarevic/logo.webp'
import namjestajJasarevicScreenshot from '@/assets/portfolio/namjestaj-jasarevic/Screenshot 2026-08-27 082056.webp'

export interface PortfolioAsset {
  id: string
  image: string
  label: string
  labelEn: string
  alt: string
  altEn: string
}

export interface PortfolioTab {
  id: string
  label: string
  labelEn: string
  assets: PortfolioAsset[]
}

export interface PortfolioProject {
  id: string
  name: string
  tag: string
  tagEn: string
  tagColor: 'amber' | 'cyan'
  desc: string
  descEn: string
  chips: string[]
  chipsEn: string[]
  externalUrl: string
  externalLabel: string
  thumb: string
  /** Intrinsic pixel size of `thumb`, only rendered as <img> when there's no beforeAfter slider — used to set explicit width/height and avoid CLS. */
  thumbWidth?: number
  thumbHeight?: number
  beforeAfter?: { before: string; after: string }
  tabs: PortfolioTab[]
  caseStudy: {
    problem: string
    problemEn: string
    solution: string
    solutionEn: string
    result: string
    resultEn: string
    stack: string[]
  }
}

export const PORTFOLIO_PROJECTS: PortfolioProject[] = [
  {
    id: 'tehnostan',
    name: 'D.O.O. Tehnostan',
    tag: 'Premium stranica',
    tagEn: 'Premium website',
    tagColor: 'amber',
    desc: 'Kamenorezačka firma koja je krenula od nule — bez logotipa, bez stranice, bez ikakvog vizualnog identiteta. Danas imaju sve: web stranicu, logo, vizit kartu i reklame.',
    descEn:
      'A stonemasonry business that started from zero — no logo, no website, no visual identity at all. Today they have everything: a website, logo, business card and roadside ads.',
    chips: ['Web stranica', 'Logo', 'Vizit karta', 'Poster', 'Reklame', 'Folije za stakla', 'Admin panel', 'Supabase'],
    chipsEn: ['Website', 'Logo', 'Business card', 'Poster', 'Ads', 'Window film ads', 'Admin panel', 'Supabase'],
    externalUrl: 'https://tehnostan.online',
    externalLabel: 'tehnostan.online',
    thumb: tehnostanScreenshot,
    beforeAfter: { before: tehnostanBefore, after: tehnostanAfter },
    tabs: [
      {
        id: 'print',
        label: 'Vizit karta',
        labelEn: 'Business card',
        assets: [
          {
            id: 'card',
            image: tehnostanCard,
            label: 'Vizit karta',
            labelEn: 'Business card',
            alt: 'Tehnostan — vizit karta',
            altEn: 'Tehnostan — business card',
          },
        ],
      },
      {
        id: 'poster',
        label: 'Poster',
        labelEn: 'Poster',
        assets: [
          {
            id: 'poster',
            image: tehnostanPoster,
            label: 'Poster',
            labelEn: 'Poster',
            alt: 'Tehnostan — poster',
            altEn: 'Tehnostan — poster',
          },
        ],
      },
      {
        id: 'ads',
        label: 'Reklame',
        labelEn: 'Ads',
        assets: [
          {
            id: 'ad1',
            image: tehnostanAd,
            label: 'Putna reklama 1',
            labelEn: 'Road ad 1',
            alt: 'Tehnostan putna reklama 1',
            altEn: 'Tehnostan road ad 1',
          },
          {
            id: 'ad2',
            image: tehnostanAd2,
            label: 'Putna reklama 2',
            labelEn: 'Road ad 2',
            alt: 'Tehnostan putna reklama 2',
            altEn: 'Tehnostan road ad 2',
          },
        ],
      },
      {
        id: 'brand',
        label: 'Logo',
        labelEn: 'Logo',
        assets: [
          {
            id: 'logo',
            image: tehnostanLogo,
            label: 'Logotip',
            labelEn: 'Logo',
            alt: 'Tehnostan logotip',
            altEn: 'Tehnostan logo',
          },
        ],
      },
      {
        id: 'folije',
        label: 'Folije za stakla',
        labelEn: 'Window film ads',
        assets: [
          {
            id: 'folija-strana',
            image: tehnostanFolijaStrana,
            label: 'Bočna strana vozila',
            labelEn: 'Side of vehicle',
            alt: 'Tehnostan reklamna folija za staklo — bočna strana vozila',
            altEn: 'Tehnostan window film ad — side of vehicle',
          },
          {
            id: 'folija-zadnja',
            image: tehnostanFolijaZadnja,
            label: 'Zadnje staklo',
            labelEn: 'Rear window',
            alt: 'Tehnostan reklamna folija za staklo — zadnje staklo',
            altEn: 'Tehnostan window film ad — rear window',
          },
        ],
      },
    ],
    caseStudy: {
      problem:
        'Firma je radila isključivo na preporuku — bez web stranice, bez logotipa, bez vizualnog identiteta. Potencijalni klijenti nisu imali gdje provjeriti kvalitet ni pronaći kontakt.',
      problemEn:
        'The business worked by word of mouth only — no website, no logo, no visual identity. Potential clients had nowhere to check quality or find contact details.',
      solution:
        'Kompletan branding paket: logo, vizit karta, reklame za pored puta, luksuzna web stranica s galerijom od 160+ fotografija, kalkulatorom cijena i admin panelom.',
      solutionEn:
        'A complete branding package: logo, business card, roadside ads, and a luxury website with a 160+ photo gallery, price calculator and admin panel.',
      result:
        'Firma sada ima kompletan digitalni i fizički identitet. Klijent samostalno ažurira cijene i fotografije. Prepoznatljivost u lokalnoj zajednici značajno porasla.',
      resultEn:
        'The business now has a complete digital and physical identity. The client updates prices and photos independently. Local recognition has grown significantly.',
      stack: ['Next.js 14', 'Supabase', 'Admin panel', 'Kalkulator cijena', '160+ fotografija', 'Vercel', 'Logo dizajn', 'Print dizajn'],
    },
  },
  {
    id: 'mm-metal',
    name: 'MM Metal',
    tag: 'Profesionalna stranica',
    tagEn: 'Professional website',
    tagColor: 'cyan',
    desc: 'Metaloprerađivačka firma koja je dobila kompletan web identitet — od logotipa do moderne stranice s galerijom, kontakt formom, Google Maps integracijom i SEO optimizacijom.',
    descEn:
      'A metalworking business that got a complete web identity — from the logo to a modern site with gallery, contact form, Google Maps and SEO optimisation.',
    chips: ['Web stranica', 'Logo', 'Galerija', 'Kontakt forma', 'Google Maps', 'SEO', 'Copywriting'],
    chipsEn: ['Website', 'Logo', 'Gallery', 'Contact form', 'Google Maps', 'SEO', 'Copywriting'],
    externalUrl: 'https://mm-metal.com',
    externalLabel: 'mm-metal.com',
    thumb: mmMetalScreenshot,
    // Must match the actual file — see scripts/resize_portfolio_images.py,
    // which caps this at 1212px wide (2x its 606px display slot).
    thumbWidth: 1212,
    thumbHeight: 667,
    tabs: [
      {
        id: 'brand',
        label: 'Logo',
        labelEn: 'Logo',
        assets: [
          {
            id: 'logo',
            image: mmMetalLogo,
            label: 'Logotip',
            labelEn: 'Logo',
            alt: 'MM Metal logotip',
            altEn: 'MM Metal logo',
          },
        ],
      },
    ],
    caseStudy: {
      problem:
        'Firma je radila bez ikakvog online prisustva — bez web stranice, bez logotipa. Potencijalni klijenti nisu imali gdje pronaći informacije o uslugama ni stupiti u kontakt.',
      problemEn:
        'The business had no online presence at all — no website, no logo. Potential clients had nowhere to find information about services or get in touch.',
      solution:
        'Profesionalna web stranica s modernim logotipom, galerijom radova, kontakt formom, Google Maps integracijom, copywritingom i SEO optimizacijom — sve u jednom paketu.',
      solutionEn:
        'A professional website with a modern logo, work gallery, contact form, Google Maps integration, copywriting and SEO optimisation — all in one package.',
      result:
        'Firma sada ima profesionalan digitalni identitet. Stranica je brza, mobilno prilagođena i optimizovana za Google — klijenti lako pronalaze firmu i stupaju u kontakt.',
      resultEn:
        'The business now has a professional digital identity. The site is fast, mobile-friendly and optimised for Google — clients easily find the business and get in touch.',
      stack: ['HTML / CSS / JS', 'Galerija radova', 'Kontakt forma', 'Google Maps', 'SEO optimizacija', 'Copywriting', 'Logo dizajn'],
    },
  },
  {
    id: 'namjestaj-jasarevic',
    name: 'Namještaj Jašarević',
    tag: 'Profesionalna stranica',
    tagEn: 'Professional website',
    tagColor: 'cyan',
    desc: 'Namještajska firma iz Živinica koja je dobila potpuno prilagođenu web stranicu — galerija proizvoda s paginacijom, glatko drag-scroll listanje i jasna prezentacija ponude, bez oslanjanja na gotov template.',
    descEn: 'A furniture business from Živinice that got a fully custom-built website — a product gallery with pagination, smooth drag-scroll browsing and a clear presentation of their range, with no off-the-shelf template underneath.',
    chips: ['Web stranica', 'Logo', 'Galerija', 'Drag-scroll', 'WebP optimizacija', 'Responzivan dizajn'],
    chipsEn: ['Website', 'Logo', 'Gallery', 'Drag-scroll', 'WebP optimisation', 'Responsive design'],
    externalUrl: 'https://namjestajjasarevic.org',
    externalLabel: 'namjestajjasarevic.org',
    thumb: namjestajJasarevicScreenshot,
    // Must match the actual file — see scripts/resize_portfolio_images.py.
    thumbWidth: 1212,
    thumbHeight: 666,
    tabs: [
      {
        id: 'brand',
        label: 'Logo',
        labelEn: 'Logo',
        assets: [
          {
            id: 'logo',
            image: namjestajJasarevicLogo,
            label: 'Logotip',
            labelEn: 'Logo',
            alt: 'Namještaj Jašarević logotip',
            altEn: 'Namještaj Jašarević logo',
          },
        ],
      },
    ],
    caseStudy: {
      problem: 'Firma nije imala web stranicu niti bilo kakav online katalog — cijela ponuda namještaja postojala je samo fizički u izložbenom prostoru, pa je zainteresovanim kupcima bilo teško unaprijed pregledati asortiman.',
      problemEn: 'The business had no website or any online catalog — the entire furniture range only existed physically in the showroom, making it hard for interested buyers to browse the range in advance.',
      solution: 'Potpuno prilagođena statička web stranica izgrađena od nule (HTML/CSS/JS): galerija proizvoda s responzivnom paginacijom i dugmetom Učitaj više, drag-scroll listanje kategorija, doradene harmonike (accordion) i sve slike optimizovane u WebP format radi brzine učitavanja.',
      solutionEn: 'A fully custom static website built from scratch (HTML/CSS/JS): a product gallery with responsive pagination and a load-more button, drag-scroll category browsing, refined accordion sections, and every image converted to WebP for faster loading.',
      result: 'Firma sada ima brzu, mobilno prilagođenu stranicu s pregledanim katalogom ponude. Kupci mogu pregledati asortiman prije dolaska u izložbeni prostor. Stranica je optimizovana za brzo učitavanje zahvaljujući WebP slikama.',
      resultEn: 'The business now has a fast, mobile-friendly site with a browsable catalog of their range. Customers can review the range before visiting the showroom in person. The site loads quickly thanks to the WebP image conversion.',
      stack: ['HTML / CSS / JS', 'Galerija proizvoda', 'Drag-scroll', 'WebP optimizacija', 'Responzivna paginacija', 'Logo dizajn'],
    },
  },
]
